import React, { useRef } from "react";

// This is a reusable component for the 6-digit OTP input boxes
const OtpInput = ({ value, onChange, onEnter }) => {
    // We use a reference array to keep track of the 6 separate HTML input elements
    const otpRefs = useRef([]);

    // This function is triggered whenever the user types a new digit in one of the boxes
    const handleOtpChange = (index, digit) => {
        // Only allow numbers to be typed (regex check for digits only)
        if (!/^\d*$/.test(digit)) return;

        // Convert the current value into an array of characters, padding with empty strings if less than 6
        const otpArray = (value || "").padEnd(6, "").split("");

        // Update the specific array slot with the very last character typed
        otpArray[index] = digit.slice(-1);

        // Re-join the array back into a single string of maximum length 6
        const newOtp = otpArray.join("").slice(0, 6);

        // Call the parent component's state updater to save the new OTP string
        onChange(newOtp);

        // If a number was entered and we are not on the last box, automatically focus the next box
        if (digit && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    // This function listens for specific keyboard presses like Backspace and Enter
    const handleOtpKeyDown = (index, e) => {
        // If the user presses the 'Backspace' key
        if (e.key === "Backspace") {
            // And if the current box is empty, but we aren't at the very first box
            if (!value[index] && index > 0) {
                // Move the cursor back to the previous box automatically
                otpRefs.current[index - 1]?.focus();
            }
        }
        // If the user presses the 'Enter' key
        else if (e.key === "Enter") {
            // Trigger the specialized function passed from the parent (usually "verifyOtp")
            if (onEnter) onEnter();
        }
    };

    // This function securely handles copy-pasting an OTP directly into the boxes
    const handleOtpPaste = (e) => {
        // Prevent the default browser paste action
        e.preventDefault();

        // Grab the pasted text from the clipboard, remove spaces, and keep only the first 6 characters
        const data = e.clipboardData.getData("text").trim().slice(0, 6);

        // Make sure the pasted text contains ONLY numbers
        if (!/^\d+$/.test(data)) return;

        // Immediately update the parent component's state with the pasted 6-digit code
        onChange(data);

        // Find out which box the cursor should jump to next (usually the last one if it's 6 digits)
        const lastFilledIndex = Math.min(data.length, 5);

        // Focus the appropriate input box
        otpRefs.current[lastFilledIndex]?.focus();
    };

    // Render the component layout to the screen
    return (
        // Wrap the inputs in a flexbox container so they comfortably sit side by side
        <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginBottom: "15px", width: "100%" }}>
            {/* Loop through an array of numbers representing the 6 positions (0-5) */}
            {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                    // React needs a unique key when looping elements
                    key={index}
                    // Attach this specific HTML element to our reference array
                    ref={(el) => (otpRefs.current[index] = el)}
                    // Set type to text (tel or number could add messy browser styling arrows)
                    type="text"
                    // Automatically pull the correct character from the state based on the index position
                    value={value[index] || ""}
                    // Limit typed characters strictly to 1 character per box
                    maxLength={1}
                    // Custom styles to make it look like a nice, big, clickable button
                    style={{
                        width: "40px",
                        height: "48px",
                        textAlign: "center",
                        fontSize: "20px",
                        fontWeight: "bold",
                        borderRadius: "6px",
                        border: "2px solid #393185",
                        color: "#393185",
                        backgroundColor: "#fff",
                        outline: "none",
                        padding: "0",
                        transition: "all 0.2s ease"
                    }}
                    // Hook up our typing function
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    // Hook up our keyboard listening function
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    // Hook up our copy-paste function
                    onPaste={handleOtpPaste}
                    // Change the border color to gold-ish when the user clicks/focuses a specific box
                    onFocus={(e) => e.target.style.borderColor = "#c6a664"}
                    // Revert the border color back to deep purple when the user clicks away
                    onBlur={(e) => e.target.style.borderColor = "#393185"}
                />
            ))}
        </div>
    );
};

// Export to be used in Login, Signup, or anywhere else
export default OtpInput;